// Dexie-backed adapters for the platform-agnostic MLS conversation
// orchestration that lives in @ping/ui. The actual logic (mint group,
// fetch peer devices, consume KeyPackage, addMembers, profile-intro
// gating) is in `packages/ui/src/conversation/mlsConversation.ts`;
// here we only thread the apps/web persistence + network singletons
// into that helper so callers (NewChatModal, WebChatRoom) keep a
// single import.

import {
  getOrCreateMlsConversation as sharedGetOrCreate,
  type GetOrCreateOptions,
  type KVStore,
  type MlsConversationHandle,
} from '@ping/ui';

import { api } from '@core/network';
import { db } from '@core/persistence/db';

import { webConversationsAdapter } from './webConversationsStore';

// Re-export the type aliases so existing callers can keep importing
// from this module without round-tripping through @ping/ui.
export type { InviteStatus, MlsConversationHandle, WelcomeRecipientSink } from '@ping/ui';

// Dexie's `settings_kv` table satisfies the KVStore contract directly
// — its row shape (key/value/updatedAt) matches what the helper writes.
// Exported so the chat sync hooks (useIncomingConversations) can share
// the same adapter instance instead of constructing a duplicate.
export const kvAdapter: KVStore = {
  async get(key) {
    const row = await db.settings_kv.get(key);
    return row ? { value: row.value } : undefined;
  },
  async put(record) {
    await db.settings_kv.put(record);
  },
  async delete(key) {
    await db.settings_kv.delete(key);
  },
};

export { webConversationsAdapter } from './webConversationsStore';

// Callers omit `kv`, `conversations`, and `api` — they're injected
// here from the Dexie + fetch singletons. Everything else still has
// to be supplied per call (client, transport, beConversationId, etc.)
// because those are request-scoped.
type WebGetOrCreateOptions = Omit<GetOrCreateOptions, 'kv' | 'conversations' | 'api'>;

export async function getOrCreateMlsConversation(
  opts: WebGetOrCreateOptions,
): Promise<MlsConversationHandle> {
  return sharedGetOrCreate({
    ...opts,
    kv: kvAdapter,
    conversations: webConversationsAdapter,
    api,
  });
}
